import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  ExpandMore,
  ReportProblem,
  ChevronRight,
} from '@mui/icons-material';
import { getEdmDashboardViolations } from '../../../../helpers/api';
import { parseString3, properCase } from '../../../../helpers/utils';
import LoaderSkeleton from '../../../common/LoaderSkeleton';

export default function ViolatorsEdmView() {
  // --- CORE STATE & LOGIC (UNTOUCHED) ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limitType, setLimitType] = useState('Nesrea');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetch();
  }, [limitType]);

  const fetch = () => {
    setLoading(true);
    getEdmDashboardViolations(limitType)
      .then((response) => {
        setLoading(false);
        const responseData = response.result.data || [];
        setData(responseData);
        if (responseData.length > 0) setExpanded(responseData[0].facilityId);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
      });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // --- TABULAR DISPLAY ---
  const renderTabularDisplay = (parameterItem) => {
    return (
      <TableContainer sx={{ borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f9fafb' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Normal</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">QAQC</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Limit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameterItem.map((record, idx) => (
              <TableRow key={idx} hover>
                <TableCell sx={{ fontSize: '0.8rem' }}>{record.parameterName}</TableCell>
                <TableCell sx={{ fontSize: '0.8rem' }}>{parseString3(record.parameterUnit)}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{record.normal}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.8rem' }}>{record.qaqc}</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'error.main' }}>
                  {limitType === 'Nesrea' ? record.nesrea : record.worldBank}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // --- CONTENT RENDERING ---
  const renderContent = () => {
    if (loading) return <Box sx={{ p: 2 }}><LoaderSkeleton /></Box>;

    if (!loading && data.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
          <Typography variant="body2">No violations recorded for this criteria</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ maxHeight: '450px', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '5px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e0e0e0', borderRadius: '10px' } }}>
        {data.map((item) => (
          <Accordion
            key={item.facilityId}
            expanded={expanded === item.facilityId}
            onChange={handleAccordionChange(item.facilityId)}
            elevation={0}
            sx={{
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px !important',
              '&:before': { display: 'none' },
              overflow: 'hidden'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: expanded === item.facilityId ? alpha('#FA1010', 0.04) : 'inherit' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 2 }}>
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {item.facilityName}
                </Typography>
                <Chip 
                  label={`${item.violationCount} Violations`} 
                  size="small" 
                  color="error" 
                  sx={{ fontWeight: 700, height: 20, fontSize: '0.65rem' }} 
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, bgcolor: '#fff' }}>
              {item.measurements.map((measurementItem, mIdx) => (
                <Box key={mIdx} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pl: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                    <Typography variant="caption" fontWeight={700} color="primary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {properCase(measurementItem.measurementName)}
                    </Typography>
                  </Box>
                  {renderTabularDisplay(measurementItem.parameters)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  return (
    <Card 
      elevation={0} 
      sx={{ 
        height: '100%', 
        borderRadius: '16px', 
        border: '1px solid', 
        borderColor: 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: alpha('#FA1010', 0.1), color: '#FA1010', display: 'flex' }}>
            <ReportProblem fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#5F6368', lineHeight: 1.2 }}>
              Violations
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Emission limit exceedances
            </Typography>
          </Box>
        </Box>

        <ToggleButtonGroup
          value={limitType}
          exclusive
          onChange={(e, val) => val && setLimitType(val)}
          size="small"
          sx={{ height: 32 }}
        >
          <ToggleButton value="Nesrea" sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}>Nesrea</ToggleButton>
          <ToggleButton value="World" sx={{ textTransform: 'none', fontWeight: 700, px: 2 }}>World Bank</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Divider />

      <Box sx={{ p: 2, flexGrow: 1 }}>
        {renderContent()}
      </Box>
    </Card>
  );
}